import type { OrgChatPresetDataInput } from '@/components/org-chat/org-chat-types';
import { normalizeProductPresetSubtypeSelections } from '@/lib/product-preset-subtypes';
import { type OrgChatSendPayload } from './org-chat-api-attachment';
import {
  hasItems,
  getStablePreviewUrl,
  uniqueStrings,
} from './attachment-identity-keys';
export const buildPresetFields = (
  presetData?: OrgChatPresetDataInput
): Omit<
  OrgChatSendPayload,
  'orgThreadId' | 'orgId' | 'prompt' | 'attachments'
> => {
  const nextPresetData = { ...(presetData ?? {}) };
  const fields: Omit<
    OrgChatSendPayload,
    'orgThreadId' | 'orgId' | 'prompt' | 'attachments'
  > = {};

  if (nextPresetData.artifactPanelState) {
    fields.artifactPanelState = structuredClone(
      nextPresetData.artifactPanelState
    );
  }
  if (nextPresetData.workspaceNodeReferences !== undefined) {
    fields.workspaceNodeReferences = nextPresetData.workspaceNodeReferences.map(
      (reference) => ({ ...reference })
    );
  }
  if (nextPresetData.productPresetId) {
    fields.productPresetId = nextPresetData.productPresetId;
  }
  if (hasItems(nextPresetData.productPresetIds)) {
    fields.productPresetIds = nextPresetData.productPresetIds;
  }
  if (hasItems(nextPresetData.productPresetSubtypeSelections)) {
    fields.productPresetSubtypeSelections =
      normalizeProductPresetSubtypeSelections(
        nextPresetData.productPresetSubtypeSelections
      );
  }
  if (hasItems(nextPresetData.draftContributorUserIds)) {
    fields.draftContributorUserIds = nextPresetData.draftContributorUserIds;
  }
  if (nextPresetData.stylePresetId) {
    fields.stylePresetId = nextPresetData.stylePresetId;
  }
  if (nextPresetData.colorPresetId) {
    fields.colorPresetId = nextPresetData.colorPresetId;
  }
  if (nextPresetData.aestheticId) {
    fields.aestheticId = nextPresetData.aestheticId;
  }
  if (nextPresetData.brandingId) {
    fields.brandingId = nextPresetData.brandingId;
    if (nextPresetData.excludedBrandBlockKeys?.length) {
      fields.excludedBrandBlockKeys = nextPresetData.excludedBrandBlockKeys;
    }
  }
  if (nextPresetData.forceDeepResearch) {
    fields.forceDeepResearch = true;
  }
  if (nextPresetData.fixedModelAlias) {
    fields.fixedModelAlias = nextPresetData.fixedModelAlias;
  }
  if (hasItems(nextPresetData.selectedColorPresetIds)) {
    fields.selectedColorPresetIds = nextPresetData.selectedColorPresetIds;
  }
  if (hasItems(nextPresetData.selectedColorPalette)) {
    fields.selectedColorPalette = nextPresetData.selectedColorPalette;
  }
  if (nextPresetData.additionalInstructions) {
    fields.additionalInstructions = nextPresetData.additionalInstructions;
  }
  if (hasItems(nextPresetData.selectedUserSkills)) {
    fields.selectedUserSkills = nextPresetData.selectedUserSkills;
  }
  if (hasItems(nextPresetData.selectedAvatars)) {
    fields.selectedAvatars = nextPresetData.selectedAvatars.map(
      ({ avatarId, name, vfsFolderPath }) => ({
        avatarId,
        name,
        vfsFolderPath,
      })
    );
  }
  if (hasItems(nextPresetData.referenceStorageKeys)) {
    fields.referenceStorageKeys = nextPresetData.referenceStorageKeys;
  }
  if (hasItems(nextPresetData.referenceStorageIds)) {
    fields.referenceStorageIds = nextPresetData.referenceStorageIds;
  }
  if (hasItems(nextPresetData.referenceVfsPaths)) {
    fields.referenceVfsPaths = nextPresetData.referenceVfsPaths;
  }
  if (hasItems(nextPresetData.selectedContextImageStorageKeys)) {
    fields.selectedContextImageStorageKeys =
      nextPresetData.selectedContextImageStorageKeys;
  }
  if (hasItems(nextPresetData.selectedContextImageStorageIds)) {
    fields.selectedContextImageStorageIds =
      nextPresetData.selectedContextImageStorageIds;
  }
  if (hasItems(nextPresetData.selectedContextWorkspaceFileIds)) {
    fields.selectedContextWorkspaceFileIds =
      nextPresetData.selectedContextWorkspaceFileIds;
  }
  if (hasItems(nextPresetData.selectedContextImageVfsPaths)) {
    fields.selectedContextImageVfsPaths =
      nextPresetData.selectedContextImageVfsPaths;
  }
  if (nextPresetData.selectedSuggestion) {
    const selectedSuggestion = { ...nextPresetData.selectedSuggestion };
    // `isPrimary` controls local presentation only. Keep the send transport
    // compatible with deployments whose selected-suggestion validator predates
    // that optional UI hint; primary actions still fall back to their label.
    delete selectedSuggestion.isPrimary;
    fields.selectedSuggestion = selectedSuggestion;
  }
  if (hasItems(nextPresetData.productReferences)) {
    fields.productReferences = nextPresetData.productReferences.map((ref) => {
      const { previewUrl: rawPreviewUrl, ...rest } = ref;
      const previewUrl = getStablePreviewUrl(rawPreviewUrl);
      return { ...rest, ...(previewUrl ? { previewUrl } : {}) };
    });
  }
  if (hasItems(nextPresetData.styleReferences)) {
    fields.styleReferences = nextPresetData.styleReferences.map((ref) => {
      const { previewUrl: rawPreviewUrl, ...rest } = ref;
      const previewUrl = getStablePreviewUrl(rawPreviewUrl);
      return { ...rest, ...(previewUrl ? { previewUrl } : {}) };
    });
  }
  if (hasItems(nextPresetData.colorReferences)) {
    fields.colorReferences = nextPresetData.colorReferences.map((ref) => {
      const { previewUrl: rawPreviewUrl, ...rest } = ref;
      const previewUrl = getStablePreviewUrl(rawPreviewUrl);
      return { ...rest, ...(previewUrl ? { previewUrl } : {}) };
    });
  }
  if (nextPresetData.selectedConnectors !== undefined) {
    fields.selectedConnectors = nextPresetData.selectedConnectors;
  }
  if (hasItems(nextPresetData.linkStorageKeysUploaded)) {
    fields.linkStorageKeysUploaded = nextPresetData.linkStorageKeysUploaded;
  }
  if (hasItems(nextPresetData.linkStorageKeysSelected)) {
    fields.linkStorageKeysSelected = nextPresetData.linkStorageKeysSelected;
  }
  if (hasItems(nextPresetData.linkStorageIdsUploaded)) {
    fields.linkStorageIdsUploaded = nextPresetData.linkStorageIdsUploaded;
  }
  if (hasItems(nextPresetData.linkStorageIdsSelected)) {
    fields.linkStorageIdsSelected = nextPresetData.linkStorageIdsSelected;
  }

  return fields;
};

export const mergeReferencePayloadFields = <
  T extends Pick<
    OrgChatSendPayload,
    'referenceVfsPaths' | 'selectedContextWorkspaceFileIds'
  >,
>(
  fields: T,
  extra: Pick<
    OrgChatSendPayload,
    'referenceVfsPaths' | 'selectedContextWorkspaceFileIds'
  >
): T => {
  const referenceVfsPaths = uniqueStrings([
    ...(fields.referenceVfsPaths ?? []),
    ...(extra.referenceVfsPaths ?? []),
  ]);
  const selectedContextWorkspaceFileIds = Array.from(
    new Set([
      ...(fields.selectedContextWorkspaceFileIds ?? []),
      ...(extra.selectedContextWorkspaceFileIds ?? []),
    ])
  );

  return {
    ...fields,
    ...(referenceVfsPaths.length > 0 ? { referenceVfsPaths } : {}),
    ...(selectedContextWorkspaceFileIds.length > 0
      ? { selectedContextWorkspaceFileIds }
      : {}),
  };
};
